import { MainLayoutContainer } from "components/Layout";
import { useSurveyDetail } from "./useSurveyDetail";
import {
  Box,
  CardContent,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Divider,
  Chip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { ResponsiveButtonLink } from "components/Button";
import { CardOutline } from "components/Card";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { ROLES } from "types/user";

export const SurveyDetail = () => {
  const {
    survey,
    loading,
    role,
    isOrgView,
    organizationId,
  } = useSurveyDetail();

  if (loading || !survey) {
    return <div>loading...</div>;
  }

  return (
    <MainLayoutContainer
      title={survey.name}
      breadcrumb={
        isOrgView
          ? BREADCRUMB_PAGE.ORGANIZATION_SURVEY_CREATE_AND_EDIT
          : BREADCRUMB_PAGE.SURVEY_CREATE_AND_EDIT
      }
      loading={loading}
      buttonActions={
        (role === ROLES.ADMIN ||
          role === ROLES.DIRECTOR ||
          role === ROLES.MANAGER) && (
          <ResponsiveButtonLink
            icon={<Edit />}
            to={
              organizationId
                ? `/organization/${organizationId}/survey/${survey.survey_id}/edit`
                : `/survey/${survey.survey_id}/edit`
            }
          >
            Edit
          </ResponsiveButtonLink>
        )
      }
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <CardOutline>
              <CardContent>
                <Typography gutterBottom component="div" fontWeight={600} variant="h6">
                  Survey Information
                </Typography>
                <Box mt={2}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {survey.name}
                  </Typography>
                </Box>
              </CardContent>
            </CardOutline>
          </Grid>

          <Grid size={12}>
            <CardOutline>
              <CardContent>
                <Typography gutterBottom component="div" fontWeight={600} variant="h6">
                  Questions ({survey.questions?.length || 0})
                </Typography>
                <Divider sx={{ my: 2 }} />
                {!survey.questions || survey.questions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No questions added to this survey yet.
                  </Typography>
                ) : (
                  <List>
                    {survey.questions
                      .sort((a, b) => (a.question?.order || 0) - (b.question?.order || 0))
                      .map((sq, index) => (
                        <Box key={sq.survey_question_id || index}>
                          <ListItem
                            sx={{
                              flexDirection: "column",
                              alignItems: "flex-start",
                              py: 2,
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                              <Chip
                                label={`#${index + 1}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={
                                  sq.question?.type === "multiple_choice"
                                    ? "Multiple Choice (select multiple)"
                                    : sq.question?.type === "single_choice"
                                    ? "Multiple Choice (select one)"
                                    : sq.question?.type === "likert"
                                    ? "Likert Scale"
                                    : "Fill in the blank"
                                }
                                size="small"
                                color={
                                  sq.question?.type === "multiple_choice"
                                    ? "secondary"
                                    : sq.question?.type === "single_choice"
                                    ? "info"
                                    : "default"
                                }
                              />
                            </Stack>
                            <Box mt={1} width="100%">
                              <Typography variant="subtitle1" fontWeight={600}>
                                {sq.question?.title || "Untitled Question"}
                              </Typography>
                            </Box>
                            {sq.question?.body && (
                              <Box mt={1} width="100%">
                                <Typography variant="body2" color="text.secondary">
                                  {sq.question.body}
                                </Typography>
                              </Box>
                            )}
                            {(sq.question?.type === "multiple_choice" || sq.question?.type === "single_choice") &&
                              sq.question?.options &&
                              sq.question.options.length > 0 && (
                                <Box mt={2} width="100%">
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Options:
                                  </Typography>
                                  <List dense sx={{ pl: 2 }}>
                                    {sq.question.options
                                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                                      .map((option, optIndex) => (
                                        <ListItem key={option.option_id || optIndex} sx={{ py: 0.5 }}>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2">
                                                {String.fromCharCode(65 + optIndex)}. {option.text}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                  </List>
                                </Box>
                              )}
                          </ListItem>
                          {index < survey.questions!.length - 1 && <Divider />}
                        </Box>
                      ))}
                  </List>
                )}
              </CardContent>
            </CardOutline>
          </Grid>
        </Grid>
      </Box>
    </MainLayoutContainer>
  );
};
